import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { Card, CreateCardBody, UpdateCardBody } from '@klopotun/contracts';
import { cardQueries } from '../queries/card-queries.js';
import { createCard } from '../requests/create-card.js';
import { updateCard } from '../requests/update-card.js';

export type SaveCardInput =
  { mode: 'create'; body: CreateCardBody } | { mode: 'update'; id: string; body: UpdateCardBody };

const saveCard = (input: SaveCardInput): Promise<Card> => {
  if (input.mode === 'create') {
    return createCard(input.body);
  }
  return updateCard(input.id, input.body);
};

const withCard = (cards: Card[] | undefined, card: Card): Card[] | undefined => {
  if (cards === undefined || cards.some((known) => known.id === card.id)) {
    return cards;
  }

  return [...cards, card];
};

export const useSaveCardMutation = (): UseMutationResult<Card, Error, SaveCardInput> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveCard,
    onSuccess: (card, input) => {
      // A picker that selects the new card the moment it exists must find it among its options
      // before the refetch lands; otherwise the select shows nothing for a beat.
      if (input.mode === 'create') {
        queryClient.setQueriesData<Card[]>({ queryKey: cardQueries.lists() }, (cards) =>
          withCard(cards, card),
        );
      }

      return queryClient.invalidateQueries({ queryKey: cardQueries.lists() });
    },
  });
};
